import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Building2, Mail, Globe, MapPin, ArrowRight, ArrowLeft, Check, Rocket, Phone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Stepper, Step } from '@/components/ui/stepper'
import { TimezoneSelector } from '@/components/settings/TimezoneSelector'
import { agencyConfigApi } from '@/lib/api'
import { isValidEmail, sanitizePhoneNumber } from '@/lib/utils'
import { UpdateAgencyConfigurationRequest } from '@/types'

// Step Components
function WelcomeStep() {
  return (
    <div className="text-center py-8">
      <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
        <Rocket className="h-10 w-10 text-primary" />
      </div>
      <h2 className="text-2xl font-bold mb-4">Welcome to Blueprint Desk!</h2>
      <p className="text-muted-foreground max-w-md mx-auto mb-6">
        We're excited to have you on board. Let's take a few minutes to set up your agency workspace.
        This will help personalize your experience and get you started quickly.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto mt-8">
        <div className="p-4 rounded-lg bg-muted/50">
          <Building2 className="h-6 w-6 text-primary mx-auto mb-2" />
          <p className="text-sm font-medium">Agency Profile</p>
        </div>
        <div className="p-4 rounded-lg bg-muted/50">
          <Mail className="h-6 w-6 text-primary mx-auto mb-2" />
          <p className="text-sm font-medium">Contact Info</p>
        </div>
        <div className="p-4 rounded-lg bg-muted/50">
          <Globe className="h-6 w-6 text-primary mx-auto mb-2" />
          <p className="text-sm font-medium">Business Details</p>
        </div>
      </div>
    </div>
  )
}

interface StepProps {
  formData: OnboardingData
  onChange: (field: keyof OnboardingData, value: string) => void
}

function ProfileStep({ formData, onChange }: StepProps) {
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <Building2 className="h-8 w-8 text-primary mx-auto mb-2" />
        <h2 className="text-xl font-semibold">Agency Profile</h2>
        <p className="text-sm text-muted-foreground">Tell us about your agency</p>
      </div>
      <div className="grid gap-4 max-w-md mx-auto">
        <div className="space-y-2">
          <Label htmlFor="agency_name">Agency Name *</Label>
          <Input
            id="agency_name"
            placeholder="Enter your agency name"
            value={formData.agency_name}
            onChange={(e) => onChange('agency_name', e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="logo_url">Logo URL (optional)</Label>
          <Input
            id="logo_url"
            placeholder="https://example.com/logo.png"
            value={formData.logo_url}
            onChange={(e) => onChange('logo_url', e.target.value)}
          />
          <p className="text-xs text-muted-foreground">You can update this later in settings</p>
        </div>
      </div>
    </div>
  )
}

function ContactStep({ formData, onChange }: StepProps) {
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <Mail className="h-8 w-8 text-primary mx-auto mb-2" />
        <h2 className="text-xl font-semibold">Contact Information</h2>
        <p className="text-sm text-muted-foreground">How can clients reach you?</p>
      </div>
      <div className="grid gap-4 max-w-lg mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="contact@agency.com"
              value={formData.email}
              onChange={(e) => onChange('email', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              placeholder="+1 (555) 000-0000"
              value={formData.phone}
              onChange={(e) => onChange('phone', sanitizePhoneNumber(e.target.value))}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            placeholder="https://www.youragency.com"
            value={formData.website}
            onChange={(e) => onChange('website', e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="address_line1">Address Line 1</Label>
          <Input
            id="address_line1"
            placeholder="123 Main Street"
            value={formData.address_line1}
            onChange={(e) => onChange('address_line1', e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="address_line2">Address Line 2</Label>
          <Input
            id="address_line2"
            placeholder="Suite 100"
            value={formData.address_line2}
            onChange={(e) => onChange('address_line2', e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              placeholder="New York"
              value={formData.city}
              onChange={(e) => onChange('city', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="state">State/Province</Label>
            <Input
              id="state"
              placeholder="NY"
              value={formData.state}
              onChange={(e) => onChange('state', e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="postal_code">Postal Code</Label>
            <Input
              id="postal_code"
              placeholder="10001"
              value={formData.postal_code}
              onChange={(e) => onChange('postal_code', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <Input
              id="country"
              placeholder="United States"
              value={formData.country}
              onChange={(e) => onChange('country', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function BusinessStep({ formData, onChange }: StepProps) {
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <Globe className="h-8 w-8 text-primary mx-auto mb-2" />
        <h2 className="text-xl font-semibold">Business Details</h2>
        <p className="text-sm text-muted-foreground">Configure your business settings</p>
      </div>
      <div className="grid gap-6 max-w-md mx-auto">
        <div className="space-y-2">
          <Label>Timezone</Label>
          <TimezoneSelector
            value={formData.timezone}
            onChange={(value) => onChange('timezone', value)}
          />
          <p className="text-xs text-muted-foreground">
            All times in the app will be displayed in this timezone
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="tax_id">Tax ID (optional)</Label>
          <Input
            id="tax_id"
            placeholder="Enter your tax identification number"
            value={formData.tax_id}
            onChange={(e) => onChange('tax_id', e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            This will appear on invoices and quotes
          </p>
        </div>
      </div>
    </div>
  )
}

function CompleteStep({ formData }: { formData: OnboardingData }) {
  return (
    <div className="text-center py-8">
      <div className="mx-auto w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6">
        <Check className="h-10 w-10 text-green-600 dark:text-green-400" />
      </div>
      <h2 className="text-2xl font-bold mb-4">You're All Set!</h2>
      <p className="text-muted-foreground max-w-md mx-auto mb-8">
        Your agency workspace is ready. You can always update these settings later from the Settings page.
      </p>

      {/* Summary */}
      <div className="bg-muted/50 rounded-lg p-6 max-w-md mx-auto text-left">
        <h3 className="font-semibold mb-4">Setup Summary</h3>
        <div className="space-y-3 text-sm">
          {formData.agency_name && (
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Agency:</span>
              <span className="font-medium">{formData.agency_name}</span>
            </div>
          )}
          {formData.email && (
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Email:</span>
              <span className="font-medium">{formData.email}</span>
            </div>
          )}
          {formData.phone && (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Phone:</span>
              <span className="font-medium">{formData.phone}</span>
            </div>
          )}
          {formData.city && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Location:</span>
              <span className="font-medium">{formData.city}{formData.state ? `, ${formData.state}` : ''}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Timezone:</span>
            <span className="font-medium">{formData.timezone}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

const STEPS: Step[] = [
  { id: 'welcome', title: 'Welcome' },
  { id: 'profile', title: 'Agency Profile' },
  { id: 'contact', title: 'Contact Info' },
  { id: 'business', title: 'Business Details' },
  { id: 'complete', title: 'Complete' },
]

interface OnboardingData {
  agency_name: string
  logo_url: string
  email: string
  phone: string
  website: string
  address_line1: string
  address_line2: string
  city: string
  state: string
  postal_code: string
  country: string
  timezone: string
  tax_id: string
}

const initialData: OnboardingData = {
  agency_name: '',
  logo_url: '',
  email: '',
  phone: '',
  website: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
  timezone: 'UTC',
  tax_id: '',
}

export default function OnboardingWizardPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [currentStep, setCurrentStep] = useState(0)
  const [formData, setFormData] = useState<OnboardingData>(initialData)

  // Check if onboarding is already completed and redirect to dashboard
  const { data: agencyConfig } = useQuery({
    queryKey: ['agencyConfig'],
    queryFn: agencyConfigApi.get,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  useEffect(() => {
    // If onboarding is already completed, redirect to dashboard
    if (agencyConfig?.onboarding_completed === true) {
      navigate('/dashboard', { replace: true })
    }
  }, [agencyConfig, navigate])

  const updateMutation = useMutation({
    mutationFn: (data: UpdateAgencyConfigurationRequest) => agencyConfigApi.update(data),
    // Don't auto-invalidate here to avoid race conditions
    // We'll handle cache updates manually in each handler
  })

  const handleInputChange = (field: keyof OnboardingData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // Helper function to filter out empty string values
  // This ensures we only send fields with actual values to the API
  // so that COALESCE in the backend SQL works correctly
  const getPayload = (data: OnboardingData, onboardingCompleted: boolean): UpdateAgencyConfigurationRequest => {
    const payload: UpdateAgencyConfigurationRequest = {
      onboarding_completed: onboardingCompleted,
    }

    // Only include fields that have non-empty values
    if (data.agency_name.trim()) payload.agency_name = data.agency_name.trim()
    if (data.logo_url.trim()) payload.logo_url = data.logo_url.trim()
    if (data.email.trim()) payload.email = data.email.trim()
    if (data.phone.trim()) payload.phone = data.phone.trim()
    if (data.website.trim()) payload.website = data.website.trim()
    if (data.address_line1.trim()) payload.address_line1 = data.address_line1.trim()
    if (data.address_line2.trim()) payload.address_line2 = data.address_line2.trim()
    if (data.city.trim()) payload.city = data.city.trim()
    if (data.state.trim()) payload.state = data.state.trim()
    if (data.postal_code.trim()) payload.postal_code = data.postal_code.trim()
    if (data.country.trim()) payload.country = data.country.trim()
    if (data.timezone.trim()) payload.timezone = data.timezone.trim()
    if (data.tax_id.trim()) payload.tax_id = data.tax_id.trim()

    return payload
  }

  const handleNext = async () => {
    if (currentStep < STEPS.length - 1) {
      // Validate agency name on Profile step (step 1)
      if (currentStep === 1 && !formData.agency_name.trim()) {
        toast.error('Agency Name is required')
        return
      }

      // Validate email on Contact step (step 2)
      if (currentStep === 2 && formData.email.trim() && !isValidEmail(formData.email)) {
        toast.error('Please enter a valid email address')
        return
      }

      // Save progress at each step
      if (currentStep > 0 && currentStep < STEPS.length - 1) {
        try {
          await updateMutation.mutateAsync(getPayload(formData, false))
        } catch (error: unknown) {
          const err = error as { response?: { data?: { message?: string } }; message?: string }
          const errorMessage = err.response?.data?.message || err.message || 'Failed to save progress'
          toast.error(errorMessage)
          return
        }
      }
      setCurrentStep((prev) => prev + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1)
    }
  }

  const handleComplete = async () => {
    // Validate agency name before completing
    if (!formData.agency_name.trim()) {
      toast.error('Agency Name is required')
      return
    }

    // Validate email if provided
    if (formData.email.trim() && !isValidEmail(formData.email)) {
      toast.error('Please enter a valid email address')
      return
    }

    try {
      const updatedConfig = await updateMutation.mutateAsync(getPayload(formData, true))
      // Immediately update the cache with the response to prevent race condition
      // This ensures OnboardingGuard sees the updated onboarding_completed status
      queryClient.setQueryData(['agencyConfig'], updatedConfig)

      // Also invalidate to ensure fresh data on next fetch
      queryClient.invalidateQueries({ queryKey: ['agencyConfig'] })

      // Wait a bit to ensure the cache update propagates
      await new Promise(resolve => setTimeout(resolve, 100))

      toast.success('Onboarding completed successfully!')
      navigate('/dashboard')
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string }
      const errorMessage = err.response?.data?.message || err.message || 'Failed to complete onboarding'
      toast.error(errorMessage)
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeStep />
      case 1:
        return <ProfileStep formData={formData} onChange={handleInputChange} />
      case 2:
        return <ContactStep formData={formData} onChange={handleInputChange} />
      case 3:
        return <BusinessStep formData={formData} onChange={handleInputChange} />
      case 4:
        return <CompleteStep formData={formData} />
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-950">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Welcome to Blueprint Desk</h1>
          <p className="text-muted-foreground">Let's set up your agency workspace</p>
        </div>

        {/* Stepper */}
        <div className="mb-8">
          <Stepper steps={STEPS} currentStep={currentStep} />
        </div>

        {/* Content Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">{renderStepContent()}</CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 0}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          {currentStep < STEPS.length - 1 ? (
            <Button onClick={handleNext} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Next'} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleComplete} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Completing...' : 'Complete Setup'} <Check className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

